import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { RotateCcw, Save } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

// What the Brand check flags in a Kingdom Factors book. KF admin edits the
// rules; an empty rule goes back to the default. The topic list stays fixed.
export default function BrandCheckRulesShow({ currentUser, rules = [], topics = [], maxLength }) {
  const [form, setForm] = useState(() => Object.fromEntries(rules.map((rule) => [rule.key, rule.value])))
  const dirty = rules.some((rule) => form[rule.key] !== rule.value)

  const save = (e) => {
    e.preventDefault()
    router.patch("/app/admin/brand_check_rules", { rules: form }, { preserveScroll: true })
  }

  return (
    <AdminLayout currentUser={currentUser}>
      <Head title="Brand check rules - Kingdom Factor" />
      <form onSubmit={save} className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Brand check rules</h1>
          <p className="text-zinc-600">
            When a coach approves a chapter, the AI reads it against these rules and flags words that may not fit Kingdom Factor. A change here applies to the next check. Flags already made stay as they are. Serious flags that a coach kept wait on{" "}
            <Link href="/app/admin/brand_flags" className="font-medium text-kf-red underline">Book flags</Link>.
          </p>
        </div>

        {rules.map((rule) => (
          <Card key={rule.key}>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <CardTitle className="text-lg"><Label htmlFor={`rule_${rule.key}`} className="text-lg font-semibold">{rule.label}</Label></CardTitle>
              {form[rule.key] !== rule.default && (
                <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, [rule.key]: rule.default })}>
                  <RotateCcw className="h-4 w-4" /> Use the default
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-zinc-600">{rule.help}</p>
              <Textarea id={`rule_${rule.key}`} rows={Math.min(16, Math.max(5, form[rule.key].split("\n").length + 1))}
                maxLength={maxLength} value={form[rule.key]} onChange={(e) => setForm({ ...form, [rule.key]: e.target.value })} />
              <p className="text-xs text-zinc-500">{rule.custom ? "Changed by KF admin." : "The default."}</p>
            </CardContent>
          </Card>
        ))}

        <Card>
          <CardHeader><CardTitle className="text-lg">Topics</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-zinc-600">Each flag gets one of these topics, and the coach sees it. A flag from a new rule that fits no topic shows as "Other". To change the list, ask Jordan.</p>
            <ul className="list-disc space-y-1 pl-5 text-zinc-700">
              {topics.map((topic) => <li key={topic}>{topic}</li>)}
            </ul>
          </CardContent>
        </Card>

        <div className="sticky bottom-0 flex items-center gap-3 border-t bg-white py-3">
          <Button type="submit" disabled={!dirty}><Save className="h-4 w-4" /> Save the rules</Button>
          {dirty && <span className="text-sm text-zinc-600">You have changes that are not saved.</span>}
        </div>
      </form>
    </AdminLayout>
  )
}
